/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import Moment from 'moment' // Npm: moment.js library.
import _ from 'underscore' // Npm: underscore library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import DownloadToExcel from "components/DownloadToExcel";
import Modal from 'components/Modal'
import VendorInvoiceUpsert from 'components/VendorInvoiceUpsert'
import VendorInvoiceDelete from 'components/VendorInvoiceDelete'
import VendorInvoiceDropDownOptions from 'components/VendorInvoiceDropDownOptions'


/*
 * STYLES
 */
import { buttonStyle } from "./index.style";


/*
 * GRAPHS
 */
import VendorInvoiceReadQuery from './__query__/index.vendorInvoice.read.query'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags =
    Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
      (i) => i.key,
    );

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const {
    onOpen: onVendorInvoiceCreateOpen,
    isOpen: isVendorInvoiceCreateOpen,
    onClose: onVendorInvoiceCreateClose,
  } = useDisclosure();
  const {
    onOpen: onVendorInvoiceUpdateOpen,
    isOpen: isVendorInvoiceUpdateOpen,
    onClose: onVendorInvoiceUpdateClose,
  } = useDisclosure();
  const {
    onOpen: onVendorInvoiceDeleteOpen,
    isOpen: isVendorInvoiceDeleteOpen,
    onClose: onVendorInvoiceDeleteClose,
  } = useDisclosure();
  const _QueryVendorInvoiceRead = useQuery(VendorInvoiceReadQuery, {
    variables: {
      vendorId: passOn?.vendorId,
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
    },
    fetchPolicy: Object.React.App.fetchPolicy,
  });
  const { refetch: _RefetchVendorInvoiceRead } = _QueryVendorInvoiceRead;
  const _tableHeaderHeightRef = React.useRef(0);
  const _rowCountRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _RefetchVendorInvoiceRead();
    } finally {
      setIsRefreshing(false);
    }
  }, [_RefetchVendorInvoiceRead]);

  // Data assignment.
  const _QueryVendorInvoiceReadRowCount = _.first(
    _QueryVendorInvoiceRead?.data?.VendorInvoiceRead,
  )?._totalCount;

  const rowCount = React.useMemo(() => {
    if (
      _QueryVendorInvoiceReadRowCount !== undefined &&
      _QueryVendorInvoiceReadRowCount !== null
    ) {
      _rowCountRef.current = _QueryVendorInvoiceReadRowCount;
    }

    return _rowCountRef.current;
  }, [_QueryVendorInvoiceReadRowCount]);

  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryVendorInvoiceRead?.data?.VendorInvoiceRead?.map((item, index) =>
          _successFlags.includes(item.status)
            ? {
                id:
                  item?.id ??
                  `vendor-invoice-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                sno: `${index + paginationModel.pageSize * paginationModel.page + 1}.`,
                invoiceNumber: item?.invoiceNumber ?? "-",
                billingStartDate: Moment(item?.billingStartDate).format(
                  "DD-MM-YYYY",
                ),
                billingEndDate: Moment(item?.billingEndDate).format(
                  "DD-MM-YYYY",
                ),
                datedOn: Moment(item?.datedOn).format("DD-MM-YYYY") ?? "-",
                amount: item?.amount ?? "-",
              }
            : null,
        ),
      ),
    [
      _QueryVendorInvoiceRead?.data?.VendorInvoiceRead,
      _successFlags,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 90,
        disableColumnMenu: true,
        sortable: false,
      },
      {
        field: "invoiceNumber",
        headerName: "Invoice Number",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "billingStartDate",
        headerName: "Billing Start Date",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 170,
      },
      {
        field: "billingEndDate",
        headerName: "Billing End Date",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 170,
      },
      {
        field: "datedOn",
        headerName: "Invoiced On",
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "amount",
        headerName: `Amount ( in ${passOn?.currency ?? "EUR"} )`,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "action",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <VendorInvoiceDropDownOptions
            vendorId={passOn?.vendorId}
            vendorInvoiceId={row?.id}
            invoiceNumber={row?.invoiceNumber}
            onVendorInvoiceUpdate={onVendorInvoiceUpdateOpen}
            onVendorInvoiceDelete={onVendorInvoiceDeleteOpen}
          />
        ),
      },
    ],
    [
      onVendorInvoiceDeleteOpen,
      onVendorInvoiceUpdateOpen,
      passOn?.currency,
      passOn?.vendorId,
    ],
  );

  // Return component.
  return (
    <>
      <Flex className="vendorInvoice base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          height="calc(100vh - 120px)"
          minH={0}
          maxH="calc(100vh - 120px)"
          overflow="hidden"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex
            pb="0px"
            justify="space-between"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <Tooltip label="Add new Invoice." fontSize="sm">
              <Button
                onClick={onVendorInvoiceCreateOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                Add Invoice
              </Button>
            </Tooltip>
            <Flex gap="12px" flexWrap="wrap">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={_QueryVendorInvoiceRead?.data?.VendorInvoiceRead?.map(
                  (item, __index) =>
                    _successFlags.includes(item.status)
                      ? {
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          "Invoice Number": item?.invoiceNumber ?? "-",
                          "Billing Start Date": Moment(
                            item?.billingStartDate,
                          ).format("DD-MM-YYYY"),
                          "Billing End Date": Moment(
                            item?.billingEndDate,
                          ).format("DD-MM-YYYY"),
                          "Invoiced On":
                            Moment(item?.datedOn).format("DD-MM-YYYY") ?? "-",
                          [`Amount ( in ${passOn?.currency ?? "EUR"} )`]:
                            item?.amount ?? "-",
                        }
                      : {},
                )}
                headersData={[
                  "S.No.",
                  "Invoice Number",
                  "Billing Start Date",
                  "Billing End Date",
                  "Invoiced On",
                  `Amount ( in ${passOn?.currency ?? "EUR"} )`,
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            loading={_QueryVendorInvoiceRead?.loading}
            sx={{ height: "100%" }}
          />
        </Flex>
      </Flex>
      <Modal
        size="sm"
        title="Add Invoice"
        isOpen={isVendorInvoiceCreateOpen}
        onClose={onVendorInvoiceCreateClose}
      >
        <VendorInvoiceUpsert
          isCreateOnly={true}
          isVendorOnly
          onSuccess={_HandleRefresh}
        />
      </Modal>
      <Modal
        size="sm"
        title="Update Invoice"
        isOpen={isVendorInvoiceUpdateOpen}
        onClose={onVendorInvoiceUpdateClose}
      >
        <VendorInvoiceUpsert isVendorOnly onSuccess={_HandleRefresh} />
      </Modal>
      <Modal
        size="md"
        title="Delete Invoice"
        isOpen={isVendorInvoiceDeleteOpen}
        onClose={onVendorInvoiceDeleteClose}
      >
        <VendorInvoiceDelete onSuccess={_HandleRefresh} />
      </Modal>
    </>
  );
};


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
